// src/pages/admin/AdminDevices.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter } from 'react-icons/fa';
import AdminSidebar from './components/AdminSidebar';
import DeviceForm from './components/DeviceForm';
import '../../styles/admin.css'; // Import admin styles

// Automatic image mapping function
const getDeviceImage = (brand, model, type) => {
  const brandLower = brand?.toLowerCase();
  const modelLower = model?.toLowerCase();
  
  // Phone mappings
  if (type === 'phone') {
    // Apple/iPhone mapping
    if (brandLower === 'apple' || brandLower === 'iphone') {
      const iphoneMap = {
        'iphone 16 pro max': '/images/I-Phones/I-Phones/iphone-16-pro-max.avif',
        'iphone 16 pro': '/images/I-Phones/I-Phones/iphone-16-pro.avif',
        'iphone 16 plus': '/images/I-Phones/I-Phones/iphone-16-plus.avif',
        'iphone 16': '/images/I-Phones/I-Phones/iphone-16.avif',
        'iphone 15 pro max': '/images/I-Phones/I-Phones/iphone-15-pro-max.avif',
        'iphone 15 pro': '/images/I-Phones/I-Phones/iphone-15-pro.avif',
        'iphone 15 plus': '/images/I-Phones/I-Phones/iphone-15-plus.avif',
        'iphone 15': '/images/I-Phones/I-Phones/IPHONE15.avif',
        'iphone 14 pro max': '/images/I-Phones/I-Phones/IPHONE14PROMAX.avif',
        'iphone 14 pro': '/images/I-Phones/I-Phones/iphone14pro.avif',
        'iphone 14 plus': '/images/I-Phones/I-Phones/iphone14plus.avif',
        'iphone 14': '/images/I-Phones/I-Phones/iphone-14.avif',
        'iphone 13 pro max': '/images/I-Phones/I-Phones/iphone-13-pro-max.avif',
        'iphone 13 pro': '/images/I-Phones/I-Phones/iphone-13-pro.avif',
        'iphone 13': '/images/I-Phones/I-Phones/IPHONE13.avif',
        'iphone 13 mini': '/images/I-Phones/I-Phones/iphone-13-mini.avif',
        'iphone 12 pro max': '/images/I-Phones/I-Phones/IPHONE12PROMAX.avif',
        'iphone 12 pro': '/images/I-Phones/I-Phones/iphone12pro.avif',
        'iphone 12': '/images/I-Phones/I-Phones/iphone-12.avif',
        'iphone 12 mini': '/images/I-Phones/I-Phones/iphone-12-mini.avif',
        'iphone 11 pro max': '/images/I-Phones/I-Phones/iphone-11-pro-max.avif',
        'iphone 11 pro': '/images/I-Phones/I-Phones/iphone11pro.avif',
        'iphone 11': '/images/I-Phones/I-Phones/IPHONE11.avif',
        'iphone xs max': '/images/I-Phones/I-Phones/iphonexsmax.jpeg',
        'iphone xs': '/images/I-Phones/I-Phones/IPHONEXS.avif',
        'iphone xr': '/images/I-Phones/I-Phones/iphone-xr.avif',
        'iphone x': '/images/I-Phones/I-Phones/iphone-x.avif',
        'iphone 8 plus': '/images/I-Phones/I-Phones/iphone8plus.jpeg',
        'iphone 8': '/images/I-Phones/I-Phones/iphone8.avif',
        'iphone 7 plus': '/images/I-Phones/I-Phones/iphone-7-plus.avif',
        'iphone 7': '/images/I-Phones/I-Phones/iphone-7.avif',
        'iphone 6s plus': '/images/I-Phones/I-Phones/iphone-6s-plus.avif',
        'iphone 6s': '/images/I-Phones/I-Phones/iphone-6.avif',
        'iphone 6 plus': '/images/I-Phones/I-Phones/iphone-6.avif',
        'iphone 6': '/images/I-Phones/I-Phones/iphone-6.avif',
        'iphone se 3 (2022)': '/images/I-Phones/I-Phones/iphonese32022.avif',
        'iphone se (2nd generation)': '/images/I-Phones/I-Phones/IPHONESE22022.avif'
      };
      return iphoneMap[modelLower] || null;
    }
    
    // Samsung mapping
    if (brandLower === 'samsung') {
      if (modelLower.includes('galaxy s24 ultra')) return '/images/samsung/samsungs24ultra.avif';
      if (modelLower.includes('galaxy s24 plus')) return '/images/samsung/samsungs24plus.avif';
      if (modelLower.includes('galaxy s24')) return '/images/samsung/samsungs24.avif';
      if (modelLower.includes('galaxy s23 ultra')) return '/images/samsung/galaxys23ultra.avif';
      if (modelLower.includes('galaxy s23 plus')) return '/images/samsung/galaxys23plus.avif';
      if (modelLower.includes('galaxy s23')) return '/images/samsung/galaxys23.avif';
      if (modelLower.includes('galaxy s22 ultra')) return '/images/samsung/galaxys22ultra.avif';
      if (modelLower.includes('galaxy s22 plus')) return '/images/samsung/galaxys22plus.avif';
      if (modelLower.includes('galaxy s22')) return '/images/samsung/galaxys22.avif';
      if (modelLower.includes('galaxy s21 ultra')) return '/images/samsung/galaxys21ultra.avif';
      if (modelLower.includes('galaxy s21 plus')) return '/images/samsung/samsungs21plus.avif';
      if (modelLower.includes('galaxy s21')) return '/images/samsung/samsungs21.avif';
      if (modelLower.includes('galaxy s20')) return '/images/samsung/samsung-s21-5g.avif';
      if (modelLower.includes('galaxy note 20')) return '/images/samsung/galaxynote20.jpg';
      if (modelLower.includes('galaxy note 10')) return '/images/samsung/samsunggalaxynote10.avif';
      if (modelLower.includes('galaxy note 9')) return '/images/samsung/galaxynote9.avif';
      if (modelLower.includes('galaxy note 8')) return '/images/samsung/GALAXYNOTE8.avif';
      if (modelLower.includes('galaxy a')) {
        const aNumber = modelLower.match(/galaxy a(\d+)/)?.[1];
        if (aNumber) return `/images/samsung/a${aNumber}.jpeg`;
      }
      if (modelLower.includes('z flip')) return '/images/samsung/zflip6.avif';
      if (modelLower.includes('z fold')) return '/images/samsung/zfold6.jpg';
    }
    
    // Google Pixel mapping
    if (brandLower === 'google pixel' || brandLower === 'pixel') {
      if (modelLower.includes('pixel 9')) return '/images/GOOGLEPIXEL/googlepixel9.avif';
      if (modelLower.includes('pixel 8')) return '/images/GOOGLEPIXEL/google-pixel-8.avif';
      if (modelLower.includes('pixel 7')) return '/images/GOOGLEPIXEL/googlepixel7.avif';
      if (modelLower.includes('pixel 6')) return '/images/GOOGLEPIXEL/googlepixel6.avif';
      if (modelLower.includes('pixel 5')) return '/images/GOOGLEPIXEL/googlepixel5.avif';
      if (modelLower.includes('pixel 4')) return '/images/GOOGLEPIXEL/googlepixel4.avif';
      if (modelLower.includes('pixel 3')) return '/images/GOOGLEPIXEL/pixel3.avif';
    }
  }
  
  // Tablet mappings
  if (type === 'tablet') {
    if (brandLower === 'apple') {
      if (modelLower.includes('ipad pro 12.9')) return '/images/tablet/ipadpro12.95thgen.jpg';
      if (modelLower.includes('ipad pro 11')) return '/images/tablet/ipadpro11.jpg';
      if (modelLower.includes('ipad air')) return '/images/tablet/ipadair5thgen.webp';
      if (modelLower.includes('ipad mini')) return '/images/tablet/ipadmini6.webp';
      if (modelLower.includes('ipad 10')) return '/images/tablet/ipad10thgen.webp';
      if (modelLower.includes('ipad 9')) return '/images/tablet/ipad9thgen.webp';
      if (modelLower.includes('ipad 8')) return '/images/tablet/ipad8thgen.webp';
      if (modelLower.includes('ipad 7')) return '/images/tablet/ipad7thgen.webp';
    }
    if (brandLower === 'samsung') {
      if (modelLower.includes('tab s')) return '/images/tablet/samsunggalaxytabs6.webp';
      if (modelLower.includes('tab a')) return '/images/tablet/samsunggalaxytaba7.webp';
    }
  }
  
  // Watch mappings
  if (type === 'watch') {
    if (brandLower === 'apple') {
      if (modelLower.includes('series 9')) return '/images/watches/iwatchseries941mm.jpg';
      if (modelLower.includes('series 8')) return '/images/watches/iwatchseries841mm.jpg';
      if (modelLower.includes('series 7')) return '/images/watches/iwatchseries7.jpg';
      if (modelLower.includes('series 6')) return '/images/watches/iwatch series6.jpg';
      if (modelLower.includes('series se')) return '/images/watches/iwatchseriesse.jpeg';
      if (modelLower.includes('ultra')) return '/images/watches/iwatchseriesultra202249mm.jpg';
    }
  }
  
  // Console mappings
  if (type === 'console') {
    if (modelLower.includes('playstation 5')) return '/images/consoles/ps5.avif';
    if (modelLower.includes('playstation 4')) return '/images/consoles/ps4.avif';
    if (modelLower.includes('xbox series')) return '/images/consoles/xbox_series.avif';
    if (modelLower.includes('xbox one')) return '/images/consoles/xbox_one.avif';
    if (modelLower.includes('nintendo switch')) return '/images/consoles/nintendo_switch.avif';
  }
  
  return null;
};

function AdminDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchDevices();
  }, []);
  
  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      
      const response = await fetch('/api/admin/devices', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
          return;
        }
        throw new Error('Failed to fetch devices');
      }
      
      const data = await response.json();
      
      // Apply automatic image mapping to devices without images
      const devicesWithImages = data.map(device => {
        if (!device.image) {
          const autoImage = getDeviceImage(device.brand, device.model, device.type);
          return { ...device, image: autoImage };
        }
        return device;
      });
      
      setDevices(devicesWithImages);
      setError('');
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddDevice = () => {
    setEditingDevice(null);
    setShowForm(true);
  };
  
  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setShowForm(true);
  };
  
  const handleDeleteDevice = (device) => {
    setEditingDevice(device);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteDevice = async () => {
    if (!editingDevice) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/devices/${editingDevice._id || editingDevice.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete device');
      }
      
      // Remove device from state
      setDevices(devices.filter(d => (d._id || d.id) !== (editingDevice._id || editingDevice.id)));
      setSuccess('Device deleted successfully');
      setTimeout(() => setSuccess(''), 3000); // Clear success message after 3 seconds
      setShowDeleteConfirm(false);
      setEditingDevice(null);
    } catch (err) {
      console.error('Error deleting device:', err);
      setError('Failed to delete device. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFormClose = () => {
    setShowForm(false);
    setEditingDevice(null);
  };
  
  const handleFormSubmit = async (deviceData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const url = editingDevice 
        ? `/api/admin/devices/${editingDevice._id || editingDevice.id}` 
        : '/api/admin/devices';
      const method = editingDevice ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(deviceData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Device operation failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.message || `Failed to ${editingDevice ? 'update' : 'create'} device`);
      }
      
      // Refresh the devices list
      fetchDevices();
      
      // Show success message
      setSuccess(`Device ${editingDevice ? 'updated' : 'added'} successfully`);
      setTimeout(() => setSuccess(''), 3000); // Clear success message after 3 seconds
      
      // Close form
      handleFormClose();
    } catch (err) {
      console.error('Device form submission error:', err);
      setError(err.message || `Error ${editingDevice ? 'updating' : 'creating'} device. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to determine device release year
  const getDeviceReleaseYear = (model, brand, type) => {
    const name = model?.toLowerCase() || '';
    
    const deviceYearMapping = {
      // iPhone mappings
      'iphone 16 pro max': 2024, 'iphone 16 pro': 2024, 'iphone 16 plus': 2024, 'iphone 16': 2024,
      'iphone 15 pro max': 2023, 'iphone 15 pro': 2023, 'iphone 15 plus': 2023, 'iphone 15': 2023,
      'iphone 14 pro max': 2022, 'iphone 14 pro': 2022, 'iphone 14 plus': 2022, 'iphone 14': 2022,
      'iphone 13 pro max': 2021, 'iphone 13 pro': 2021, 'iphone 13': 2021, 'iphone 13 mini': 2021,
      'iphone 12 pro max': 2020, 'iphone 12 pro': 2020, 'iphone 12': 2020, 'iphone 12 mini': 2020,
      'iphone 11 pro max': 2019, 'iphone 11 pro': 2019, 'iphone 11': 2019,
      'iphone xs max': 2018, 'iphone xs': 2018, 'iphone xr': 2018, 'iphone x': 2017,
      'iphone 8 plus': 2017, 'iphone 8': 2017, 'iphone 7 plus': 2016, 'iphone 7': 2016,
      'iphone 6s plus': 2015, 'iphone 6s': 2015, 'iphone 6 plus': 2014, 'iphone 6': 2014,
      'iphone se 3': 2022, 'iphone se 3 (2022)': 2022, 'iphone se (2nd generation)': 2020, 'iphone se': 2016,
      
      // Samsung mappings
      'galaxy s24 ultra': 2024, 'galaxy s24+': 2024, 'galaxy s24': 2024,
      'galaxy s23 ultra': 2023, 'galaxy s23+': 2023, 'galaxy s23': 2023,
      'galaxy s22 ultra': 2022, 'galaxy s22+': 2022, 'galaxy s22': 2022,
      'galaxy s21 ultra': 2021, 'galaxy s21+': 2021, 'galaxy s21': 2021,
      'galaxy s20 ultra': 2020, 'galaxy s20+': 2020, 'galaxy s20': 2020,
      'galaxy z fold5': 2023, 'galaxy z fold4': 2022, 'galaxy z fold3': 2021,
      'galaxy z flip5': 2023, 'galaxy z flip4': 2022, 'galaxy z flip3': 2021,
      
      // Google Pixel mappings
      'pixel 9 pro xl': 2024, 'pixel 9 pro': 2024, 'pixel 9': 2024,
      'pixel 8 pro': 2023, 'pixel 8': 2023, 'pixel fold': 2023,
      'pixel 7 pro': 2022, 'pixel 7': 2022, 'pixel 6 pro': 2021, 'pixel 6': 2021,
      
      // OnePlus mappings
      'oneplus 12': 2024, 'oneplus 12r': 2024,
      'oneplus 11': 2023, 'oneplus 11r': 2023,
      'oneplus 10 pro': 2022, 'oneplus 10t': 2022,
      
      // Nokia mappings
      'nokia xr21': 2023, 'nokia x30 5g': 2022, 'nokia xr20': 2021,
      'nokia g60 5g': 2022, 'nokia g50': 2021, 'nokia g42 5g': 2023,
      'nokia g22': 2023, 'nokia g21': 2022, 'nokia g20': 2021,
      
      // Motorola mappings
      'motorola edge 40 pro': 2023, 'motorola edge 40': 2023,
      'moto g84 5g': 2023, 'moto g73': 2023, 'moto g72': 2022,
      
      // Huawei mappings
      'huawei p60 pro': 2023, 'huawei p60': 2023,
      'huawei mate 50 pro': 2022, 'huawei mate 50': 2022,
      
      // Oppo mappings
      'oppo find x6 pro': 2023, 'oppo find x6': 2023,
      'oppo reno 10 pro': 2023, 'oppo reno 10': 2023,
      
      // Xiaomi/Redmi mappings
      'xiaomi 13 ultra': 2023, 'xiaomi 13 pro': 2023, 'xiaomi 13': 2023,
      'redmi note 12 pro': 2023, 'redmi note 12': 2023,
      
      // Tablet mappings
      'ipad pro 13"': 2024, 'ipad pro 11"': 2024, 'ipad air 13"': 2024, 'ipad air 11"': 2024,
      'ipad 10th generation': 2022, 'ipad 9th generation': 2021,
      'galaxy tab s9 ultra': 2023, 'galaxy tab s9+': 2023, 'galaxy tab s9': 2023,
      
      // Watch mappings
      'apple watch series 10': 2024, 'apple watch series 9': 2023, 'apple watch series 8': 2022,
      'apple watch ultra 2': 2024, 'apple watch ultra': 2022,
      'galaxy watch 6': 2023, 'galaxy watch 5': 2022,
      
      // Console mappings
      'playstation 5 slim': 2023, 'playstation 5': 2020,
      'xbox series x': 2020, 'xbox series s': 2020,
      'nintendo switch oled': 2021, 'nintendo switch': 2017,
    };
    
    // Check exact match
    const exactMatch = deviceYearMapping[name];
    if (exactMatch) return exactMatch;
    
    // Extract year from name
    const yearMatch = name.match(/\b(20[0-9]{2})\b/);
    if (yearMatch) return parseInt(yearMatch[1]);
    
    // Default years by type
    if (type === 'console') return 2015;
    return 2020;
  };
  
  // Sort devices by year (latest first)
  const sortDevicesByYear = (devicesToSort) => {
    return [...devicesToSort].sort((a, b) => {
      const yearA = getDeviceReleaseYear(a.model, a.brand, a.type);
      const yearB = getDeviceReleaseYear(b.model, b.brand, b.type);
      
      if (yearB !== yearA) return yearB - yearA;
      
      // Within same year, sort by model priority
      const nameA = (a.model || '').toLowerCase();
      const nameB = (b.model || '').toLowerCase();
      
      const getPriority = (name) => {
        if (name.includes('ultra')) return 5;
        if (name.includes('pro max')) return 4;
        if (name.includes('pro')) return 3;
        if (name.includes('plus') || name.includes('+')) return 2;
        if (name.includes('max')) return 2;
        return 1;
      };
      
      const priorityA = getPriority(nameA);
      const priorityB = getPriority(nameB);
      
      if (priorityB !== priorityA) return priorityB - priorityA;
      
      return nameA.localeCompare(nameB);
    });
  };
  
  // Filter devices based on search and type
  const filteredDevices = devices.filter(device => {
    const matchesSearch = searchTerm === '' || 
      device.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.model?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || device.type === filterType;
    
    return matchesSearch && matchesType;
  });
  
  // Sort filtered devices
  const sortedDevices = sortDevicesByYear(filteredDevices);
  
  // Custom CSS for the device image
  const imageStyles = {
    width: '50px',
    height: '50px',
    objectFit: 'contain',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '2px'
  };
  
  const noImageStyles = {
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    color: '#6c757d',
    fontSize: '10px'
  };
  
  return (
    <div className="admin-layout">
      <AdminSidebar activeItem="devices" />
      
      <div className="admin-content">
        <div className="section-header">
          <h1>Manage Devices</h1>
          <button 
            onClick={handleAddDevice}
            className="add-button"
          >
            Add New Device
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {/* Search and Filter Controls */}
        <div className="filters-container">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by brand or model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-box">
            <FaFilter className="filter-icon" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Devices</option>
              <option value="phone">Phones</option>
              <option value="tablet">Tablets</option>
              <option value="watch">Watches</option>
              <option value="console">Consoles</option>
            </select>
          </div>
        </div>
        
        {loading && !showForm && !showDeleteConfirm ? (
          <div className="loading">Loading devices...</div>
        ) : (
          <div className="devices-list">
            {sortedDevices.length === 0 ? (
              <div className="no-data-message">
                <p>
                  {searchTerm || filterType !== 'all' 
                    ? 'No devices found matching your criteria.'
                    : 'No devices found. Add your first device!'}
                </p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Brand</th>
                    <th>Model</th>
                    <th>Type</th>
                    <th>Repairs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDevices.map(device => (
                    <tr key={device._id || device.id}>
                      <td>
                        {device.image ? (
                          <img 
                            src={device.image} 
                            alt={`${device.brand} ${device.model}`}
                            style={imageStyles}
                            onError={(e) => {
                              // Handle broken images
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = '<div style="' + Object.entries(noImageStyles).map(([k, v]) => `${k}:${v}`).join(';') + '">No Image</div>';
                            }}
                          />
                        ) : (
                          <div style={noImageStyles}>No Image</div>
                        )}
                      </td>
                      <td>{device.brand}</td>
                      <td>{device.model}</td>
                      <td style={{textTransform: 'capitalize'}}>{device.type}</td>
                      <td>{device.repairs?.length || 0}</td>
                      <td className="actions">
                        <Link 
                          to={`/admin/repairs?deviceId=${device._id || device.id}`}
                          className="repairs-button"
                        >
                          Repairs
                        </Link>
                        <button 
                          onClick={() => handleEditDevice(device)}
                          className="edit-button"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteDevice(device)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        
        {showForm && (
          <DeviceForm
            device={editingDevice}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        )}
        
        {showDeleteConfirm && editingDevice && (
          <div className="delete-confirmation-modal">
            <div className="modal-content">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete "{editingDevice.brand} {editingDevice.model}"?</p>
              <p className="warning">This will also delete all repairs associated with this device.</p>
              
              <div className="modal-actions">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeleteDevice}
                  className="delete-button"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDevices;